/**
 * Module placement in a face's uv frame (metres on the roof surface): footprints, the roof grid, "sticky" snapping
 * that respects the face's usable area (setbacks) and other modules, filling a face, and conflict checks for "free"
 * placement. Pure, unit-tested in snap.test.ts; the server re-checks everything on save (app/design/editor).
 */
import type { Vec2 } from "./geo.ts";

export type Orientation = "portrait" | "landscape";

export interface ModuleSize {
  /** Short side, metres. */
  width_m: number;
  /** Long side, metres. */
  height_m: number;
}

/** A usable area: an outer ring and holes, in uv. */
export interface Area {
  exterior: Vec2[];
  holes?: Vec2[][];
}

const RAD = Math.PI / 180;
export const EPS = 0.02; // tolerance, metres

/** Along-row and up-slope size of a module. */
export function moduleDims(orientation: Orientation, m: ModuleSize): Vec2 {
  return orientation === "portrait" ? [m.width_m, m.height_m] : [m.height_m, m.width_m];
}

/** Corners (counter-clockwise from the low-left one) of a module centred at `center`, as the backend derives them. */
export function footprint(center: Vec2, orientation: Orientation, rotationDeg: number, m: ModuleSize): Vec2[] {
  const [along, depth] = moduleDims(orientation, m);
  const c = Math.cos(rotationDeg * RAD);
  const s = Math.sin(rotationDeg * RAD);
  return (
    [
      [-along / 2, -depth / 2],
      [along / 2, -depth / 2],
      [along / 2, depth / 2],
      [-along / 2, depth / 2],
    ] as Vec2[]
  ).map(([x, y]) => [center[0] + c * x - s * y, center[1] + s * x + c * y]);
}

export function polygonArea(ring: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i]!;
    const q = ring[(i + 1) % ring.length]!;
    a += p[0] * q[1] - q[0] * p[1];
  }
  return a / 2;
}

export function pointInRing([x, y]: Vec2, ring: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const len2 = dx * dx + dy * dy;
  const t = len2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2)) : 0;
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function distToRing(p: Vec2, ring: Vec2[]): number {
  let best = Infinity;
  for (let i = 0; i < ring.length; i++) best = Math.min(best, distToSegment(p, ring[i]!, ring[(i + 1) % ring.length]!));
  return best;
}

function segmentsCross(a: Vec2, b: Vec2, c: Vec2, d: Vec2): boolean {
  const o = (p: Vec2, q: Vec2, r: Vec2) => (q[0] - p[0]) * (r[1] - p[1]) - (q[1] - p[1]) * (r[0] - p[0]);
  const d1 = o(c, d, a);
  const d2 = o(c, d, b);
  const d3 = o(a, b, c);
  const d4 = o(a, b, d);
  return d1 * d2 < -1e-12 && d3 * d4 < -1e-12;
}

function ringsCross(a: Vec2[], b: Vec2[]): boolean {
  for (let i = 0; i < a.length; i++)
    for (let j = 0; j < b.length; j++)
      if (segmentsCross(a[i]!, a[(i + 1) % a.length]!, b[j]!, b[(j + 1) % b.length]!)) return true;
  return false;
}

/** A point inside the ring, or within `tol` of its boundary. */
function insideWithTolerance(p: Vec2, ring: Vec2[], tol: number): boolean {
  return pointInRing(p, ring) || distToRing(p, ring) <= tol;
}

/** Whether the convex polygon `poly` lies inside the area (in its exterior, clear of its holes), within `tol`. */
export function insideArea(poly: Vec2[], area: Area, tol = EPS): boolean {
  if (!poly.every((p) => insideWithTolerance(p, area.exterior, tol))) return false;
  if (ringsCross(shrink(poly, tol), area.exterior)) return false;
  for (const hole of area.holes ?? []) {
    const inner = shrink(poly, tol);
    if (inner.some((p) => pointInRing(p, hole)) || hole.some((p) => pointInRing(p, inner)) || ringsCross(inner, hole))
      return false;
  }
  return true;
}

export function insideAny(poly: Vec2[], areas: Area[], tol = EPS): boolean {
  return areas.some((a) => insideArea(poly, a, tol));
}

/** The polygon shrunk towards its centroid by about `d` metres (tolerance for touching edges). */
function shrink(poly: Vec2[], d: number): Vec2[] {
  const cx = poly.reduce((s, p) => s + p[0], 0) / poly.length;
  const cy = poly.reduce((s, p) => s + p[1], 0) / poly.length;
  return poly.map(([x, y]) => {
    const dx = x - cx;
    const dy = y - cy;
    const l = Math.hypot(dx, dy) || 1;
    const k = Math.max(0, (l - d) / l);
    return [cx + dx * k, cy + dy * k];
  });
}

/** Separating-axis test for two convex polygons; touching (within `tol`) does not count as overlap. */
export function convexOverlap(a: Vec2[], b: Vec2[], tol = EPS): boolean {
  for (const poly of [a, b]) {
    for (let i = 0; i < poly.length; i++) {
      const p = poly[i]!;
      const q = poly[(i + 1) % poly.length]!;
      const nx = q[1] - p[1];
      const ny = p[0] - q[0];
      const l = Math.hypot(nx, ny) || 1;
      const project = (r: Vec2[]) => r.map(([x, y]) => (x * nx + y * ny) / l);
      const pa = project(a);
      const pb = project(b);
      if (Math.max(...pa) <= Math.min(...pb) + tol || Math.max(...pb) <= Math.min(...pa) + tol) return false;
    }
  }
  return true;
}

/** Any polygon (convex or not) overlapping a convex one: a vertex inside, or crossing edges. */
export function overlapsPolygon(convex: Vec2[], other: Vec2[], tol = EPS): boolean {
  const inner = shrink(convex, tol);
  return other.some((p) => pointInRing(p, inner)) || inner.some((p) => pointInRing(p, other)) || ringsCross(inner, other);
}

// -------------------------------------------------------------------------------------------- the grid
/** A face's module grid: cells of `pitch` (module + gap) from `anchor`, rotated by `rotationDeg` in uv. */
export interface Grid {
  anchor: Vec2;
  pitch: Vec2;
  rotationDeg: number;
}

export interface PlacedModule {
  center: Vec2;
  orientation: Orientation;
  rotationDeg: number;
}

/**
 * The grid new modules snap to on a face: anchored at an existing module of the same orientation (rows line up
 * with the installer's array), else at the low-left corner of the usable area.
 */
export function gridFor(placed: PlacedModule[], areas: Area[], orientation: Orientation, m: ModuleSize, gap: number): Grid {
  const [along, depth] = moduleDims(orientation, m);
  const pitch: Vec2 = [along + gap, depth + gap];
  const same = placed.find((p) => p.orientation === orientation);
  if (same) return { anchor: same.center, pitch, rotationDeg: same.rotationDeg };
  const pts = areas.flatMap((a) => a.exterior);
  if (!pts.length) return { anchor: [along / 2, depth / 2], pitch, rotationDeg: 0 };
  const minU = Math.min(...pts.map((p) => p[0]));
  const minV = Math.min(...pts.map((p) => p[1]));
  return { anchor: [minU + along / 2, minV + depth / 2], pitch, rotationDeg: 0 };
}

function toGrid(g: Grid, p: Vec2): Vec2 {
  const c = Math.cos(-g.rotationDeg * RAD);
  const s = Math.sin(-g.rotationDeg * RAD);
  const dx = p[0] - g.anchor[0];
  const dy = p[1] - g.anchor[1];
  return [(c * dx - s * dy) / g.pitch[0], (s * dx + c * dy) / g.pitch[1]];
}

function fromGrid(g: Grid, [i, j]: Vec2): Vec2 {
  const c = Math.cos(g.rotationDeg * RAD);
  const s = Math.sin(g.rotationDeg * RAD);
  const x = i * g.pitch[0];
  const y = j * g.pitch[1];
  return [g.anchor[0] + c * x - s * y, g.anchor[1] + s * x + c * y];
}

/** The grid cell centre nearest to `p`. */
export function snapToGrid(g: Grid, p: Vec2): Vec2 {
  const [i, j] = toGrid(g, p);
  return fromGrid(g, [Math.round(i), Math.round(j)]);
}

export interface FaceContext {
  /** Where modules may go (setbacks applied); empty = the whole face. */
  areas: Area[];
  /** Other modules on the face (footprints in uv). */
  others: Vec2[][];
  /** Obstacles on the face (outlines in uv). */
  obstacles?: Vec2[][];
}

export function fits(poly: Vec2[], ctx: FaceContext): boolean {
  return (
    insideAny(poly, ctx.areas) &&
    !ctx.others.some((o) => convexOverlap(poly, o)) &&
    !(ctx.obstacles ?? []).some((o) => overlapsPolygon(poly, o))
  );
}

/**
 * Sticky placement: the grid cell nearest `wanted` where the module fits (usable area, no overlap); searches
 * outwards up to `radius` cells. `null` when nothing near fits.
 */
export function stickyPlace(
  wanted: Vec2,
  ctx: FaceContext,
  grid: Grid,
  orientation: Orientation,
  m: ModuleSize,
  radius = 3,
): Vec2 | null {
  const [gi, gj] = toGrid(grid, wanted);
  const ci = Math.round(gi);
  const cj = Math.round(gj);
  let best: { p: Vec2; d: number } | null = null;
  for (let di = -radius; di <= radius; di++) {
    for (let dj = -radius; dj <= radius; dj++) {
      const p = fromGrid(grid, [ci + di, cj + dj]);
      const d = Math.hypot(p[0] - wanted[0], p[1] - wanted[1]);
      if (best && d >= best.d) continue;
      if (fits(footprint(p, orientation, grid.rotationDeg, m), ctx)) best = { p, d };
    }
  }
  return best?.p ?? null;
}

/** Every free grid cell of a face's usable area where a module fits, row by row from the eave. */
export function fillFace(ctx: FaceContext, grid: Grid, orientation: Orientation, m: ModuleSize, limit = 400): Vec2[] {
  const pts = ctx.areas.flatMap((a) => a.exterior.map((p) => toGrid(grid, p)));
  if (!pts.length) return [];
  const i0 = Math.floor(Math.min(...pts.map((p) => p[0]))) - 1;
  const i1 = Math.ceil(Math.max(...pts.map((p) => p[0]))) + 1;
  const j0 = Math.floor(Math.min(...pts.map((p) => p[1]))) - 1;
  const j1 = Math.ceil(Math.max(...pts.map((p) => p[1]))) + 1;
  const placed: Vec2[] = [];
  const others = [...ctx.others];
  for (let j = j0; j <= j1; j++) {
    for (let i = i0; i <= i1; i++) {
      const p = fromGrid(grid, [i, j]);
      const poly = footprint(p, orientation, grid.rotationDeg, m);
      if (fits(poly, { ...ctx, others })) {
        placed.push(p);
        others.push(poly);
        if (placed.length >= limit) return placed;
      }
    }
  }
  return placed;
}

export type ConflictKind = "outside_face" | "setback" | "obstacle" | "overlap";

/** Why a module placed freely would be refused by sticky placement (shown as warnings). */
export function conflictsOf(poly: Vec2[], face: Area, ctx: FaceContext): ConflictKind[] {
  const kinds: ConflictKind[] = [];
  if (!insideArea(poly, face)) kinds.push("outside_face");
  else if (ctx.areas.length && !insideAny(poly, ctx.areas)) kinds.push("setback");
  if ((ctx.obstacles ?? []).some((o) => overlapsPolygon(poly, o))) kinds.push("obstacle");
  if (ctx.others.some((o) => convexOverlap(poly, o))) kinds.push("overlap");
  return kinds;
}

/**
 * Filling a face, as the layout stage packs one: on a face that already has modules, their grid; on an empty face,
 * both orientations at a few sub-module offsets, keeping the one that fits the most (ties: the preferred
 * orientation).
 */
export function bestFill(
  ctx: FaceContext,
  placed: PlacedModule[],
  preferred: Orientation,
  m: ModuleSize,
  gap: number,
  steps = 4,
): { centers: Vec2[]; orientation: Orientation; grid: Grid } {
  if (placed.length) {
    const orientation = placed[0]!.orientation;
    const grid = gridFor(placed, ctx.areas, orientation, m, gap);
    return { centers: fillFace(ctx, grid, orientation, m), orientation, grid };
  }
  let best: { centers: Vec2[]; orientation: Orientation; grid: Grid } | null = null;
  for (const orientation of [preferred, preferred === "portrait" ? "landscape" : "portrait"] as Orientation[]) {
    const base = gridFor([], ctx.areas, orientation, m, gap);
    for (let i = 0; i < steps; i++) {
      for (let j = 0; j < steps; j++) {
        const grid = { ...base, anchor: [base.anchor[0] + (base.pitch[0] * i) / steps, base.anchor[1] + (base.pitch[1] * j) / steps] as Vec2 };
        const centers = fillFace(ctx, grid, orientation, m);
        if (!best || centers.length > best.centers.length) best = { centers, orientation, grid };
      }
    }
  }
  return best!;
}
